import React from 'react'
import { Form, Input, Button, Cascader, Checkbox, Divider, Select, Radio, Upload, DatePicker, message } from 'antd'
import { PlusOutlined, MinusCircleTwoTone, CaretDownOutlined } from '@ant-design/icons'
import { mdiUpload } from '@mdi/js'
import Icon from '@mdi/react'
import { storage } from './../firebase/firebaseConfig'
import { checkArray, check } from '../utils/Functions'
import { PhoneNumberCode } from '../components/utility/Utility'
import { uploadFileToS3 } from '../services/S3Upload'

const CheckboxGroup = Checkbox.Group

// Must mirror the presign server's validation rules.
const ALLOWED_UPLOAD_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf']
const UPLOAD_ACCEPT = '.jpg,.jpeg,.png,.webp,.gif,.pdf'
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024

const normFile = (e) => {
	if (Array.isArray(e)) {
		return e
	}
	return e && e.fileList
}

export const RenderField = (field) => {
	const { type } = field

	// Enforce the presign server's type/size rules before attempting an upload
	// so rejections surface immediately instead of as a failed presign request.
	const beforeUpload = (file) => {
		if (!ALLOWED_UPLOAD_TYPES.includes(file.type)) {
			message.error('Unsupported file type. Please upload a JPG, PNG, WEBP, GIF, or PDF file.')
			return Upload.LIST_IGNORE
		}
		if (file.size > MAX_UPLOAD_BYTES) {
			message.error('File is too large. Maximum allowed size is 10 MB.')
			return Upload.LIST_IGNORE
		}
		return true
	}

	const DocumentUpload = async ({ file, onProgress, onSuccess, onError }) => {
		// S3 migration: upload via presigned PUT URL. Object key mirrors the old Firebase path.
		try {
			field.setLoading && field.setLoading(true)
			const key = `public/images/import_export/${field.uploadLink}/${field.name}/${file.name}`
			const publicUrl = await uploadFileToS3({ file, key, onProgress })
			field.setLoading && field.setLoading(false)
			onSuccess({ url: publicUrl }, file)
		} catch (error) {
			console.error('S3 upload failed', error)
			field.setLoading && field.setLoading(false)
			message.error('File upload failed. Please try again.')
			onError(error)
		}
	}

	const ChangeFileList = async ({ fileList }) => {
		// Persist the durable publicUrl (returned via onSuccess) onto each file so
		// form submission reads document.url the same way it did with Firebase.
		fileList.forEach((file, index) => {
			if (file.status === 'done' && !check(file.url)) {
				fileList[index].url = file.response?.url
			}
		})
		const isUploading = fileList.some((file) => file.status === 'uploading')
		field.setLoading && field.setLoading(isUploading)
	}

	if (type === 'dummy') {
		return <Form.Item key={field.name} name={field.name} hidden></Form.Item>
	} else if (type === 'input') {
		return (
			<Form.Item
				key={field.name}
				name={field.name}
				label={field.label}
				rules={[{ required: field.required, message: field.message }]}
				hidden={field.hidden}
				className={field.itemClassName}
			>
				<Input
					className={`${field.className || `InputField`} AddOn`}
					placeholder={field.placeholder}
					type={field.inputType}
					addonAfter={field.addonAfter}
					addonBefore={field.addonBefore}
					disabled={field.disabled}
				/>
			</Form.Item>
		)
	} else if (type === 'submit') {
		return (
			<Form.Item key={field.name} name={field.name}>
				<Button disabled={field.disabled} className='Button PrimaryButton' htmlType='submit'>
					{field.label}
				</Button>
			</Form.Item>
		)
	} else if (type === 'delete') {
		return <p className='BoldFont ErrorColor'>Click Submit to delete this driver</p>
	} else if (type === 'number') {
		return (
			<Form.Item
				key={field.name}
				label={field.label}
				name={field.name}
				addonAfter={field.addonAfter}
				className={field.itemClassName}
				rules={[
					{
						required: field.required,
						validator: (rule, value = '') => {
							if (value?.toString().length > 0) {
								if (value < 0) {
									return Promise.reject(new Error(`Number cannot be negative.`))
								}
								if (field.maxValue && value > field.maxValue) {
									return Promise.reject(new Error(`Entered value exceeds max value.`))
								}
								if (field.minValue && value < field.minValue) {
									return Promise.reject(new Error(`Entered value is less than min value.`))
								}
								if (field.dynamicMaxCheck) {
									const totalWeightsEntered = field.form.getFieldsValue('addVehicle')?.addVehicle?.reduce((a, b) => a + parseFloat(b.weight), 0) - parseFloat(value)
									const remainingWeight = field.maxValue - totalWeightsEntered
									if (value > remainingWeight) {
										return Promise.reject(new Error(`Entered weight cannot exceed ${remainingWeight}.`))
									}
								} else {
									return Promise.resolve(value)
								}
							} else if (!field.required) {
								return Promise.resolve(value)
							} else {
								return Promise.reject(new Error(field.message))
							}
						},
					},
				]}
				tooltip={field.tooltip}
				initialValue={field.initialValue}
			>
				<Input
					className='InputField AddOn'
					placeholder={field.placeholder}
					type='number'
					// onInput={(e) => e.target.value = e.target.value.slice(0, 10)}
					disabled={field.disabled === true ? true : false}
					addonBefore={field.addonBefore}
					addonAfter={field.addonAfter}
				/>
			</Form.Item>
		)
	} else if (type === 'textArea') {
		return (
			<Form.Item
				key={field.name}
				name={field.name}
				label={field.label}
				rules={[{ required: field.required, message: field.message }]}
				tooltip={field.tooltip}
				defaultValue={field.defaultValue}
			>
				<Input.TextArea
					rows={field.rows}
					className='InputField AddOn'
					disabled={field.disabled}
					placeholder={field.placeholder}
					defaultValue={field.defaultValue}
					maxLength={field.maxLength}
					type={field.inputType}
				/>
			</Form.Item>
		)
	} else if (type === 'password') {
		return (
			<Form.Item
				key={field.name}
				name={field.name}
				label={field.label}
				rules={[{ required: field.required, message: field.message }]}
				tooltip={field.tooltip}
				// initialValue={field.initialValue}
			>
				<Input.Password
					addonAfter={field.addonAfter}
					addonBefore={field.addonBefore}
					className='PasswordField AddOn'
					disabled={field.disabled}
					placeholder={field.placeholder}
					type={field.inputType}
				/>
			</Form.Item>
		)
	} else if (type === 'phoneNumber') {
		const initialValue = () => {
			let temp = field?.initialValue?.replace('+92', '')
			return temp?.length > 3 ? temp?.substring(0, 3) + ' ' + temp?.substring(3) : temp
		}
		return (
			<Form.Item
				key={field.label}
				label={field.label}
				name={field.name}
				initialValue={initialValue()}
				className={field.itemClassName}
				rules={[
					{
						required: field.required,
						validator: async (_, value = '') => {
							if (value.toString().length > 0) {
								if (value < 0) {
									return Promise.reject(new Error(`Phone Number cannot be negative.`))
								} else if (value?.toString()[0] !== '3') {
									return Promise.reject(new Error(`Phone number must start with digit 3.`))
								} else if (value.toString().replace(' ', '').length !== 10) {
									return Promise.reject(new Error(`Phone Number must be of 10 digits.`))
								} else {
									return Promise.resolve()
								}
							} else if (!field.required) {
								return Promise.resolve(value)
							} else {
								return Promise.reject(new Error(field.message))
							}
						},
					},
				]}
				tooltip={field.tooltip}
			>
				<Input
					className={field.elementClassName || 'InputField AddOn'}
					onInput={(e) => {
						let temp = e.target.value.replace(/[^0-9]/g, '').slice(0, 10)
						e.target.value = temp.length > 3 ? temp.substring(0, 3) + ' ' + temp.substring(3) : temp
					}}
					placeholder={field.placeholder}
					disabled={field.action === 'view' || field.disabled === true ? true : false}
					addonBefore={field.addonBefore || <PhoneNumberCode code={'+92'} />}
					addonAfter={field.addonAfter}
				/>
			</Form.Item>
		)
	} else if (type === 'cnic') {
		return (
			<Form.Item
				key={field.name}
				label={field.label}
				name={field.name}
				rules={[
					{
						required: field.required,
						validator: (rule, value = '') => {
							if (value.toString().length > 0) {
								const specials = /\d/g
								if (value < 0) {
									return Promise.reject(new Error(`CNIC Number cannot be negative.`))
								} else if (value.toString().length !== 13) {
									return Promise.reject(new Error(`CNIC must be of 13 digits.`))
								} else if (value?.toString().length > 0) {
									if (specials.test(value.toString())) return Promise.resolve()
									else return Promise.reject(new Error(`Invalid characters used.`))
								} else {
									return Promise.resolve()
								}
							} else if (!field.required) {
								return Promise.resolve(value)
							} else {
								return Promise.reject(new Error(field.message))
							}
						},
					},
				]}
				tooltip={field.tooltip}
			>
				<Input
					className={`${field.className || `InputField`} AddOn`}
					type='number'
					onInput={(e) => (e.target.value = e.target.value.slice(0, 13))}
					disabled={field.disabled === true ? true : false}
					placeholder={field.placeholder}
					addonBefore={field.addonBefore}
					addonAfter={field.addonAfter}
				/>
			</Form.Item>
		)
	} else if (type === 'select') {
		return (
			<Form.Item
				key={field.name}
				label={field.label}
				name={field.name}
				rules={[{ required: field.required, message: field.message }]}
				initialValue={field.initialValue}
				tooltip={field.tooltip}
				noStyle={field.noStyle}
				className={field.itemClassName}
			>
				<Select
					className={`${field.className ? field.className : 'SelectField'}`}
					suffixIcon={() => <CaretDownOutlined />}
					mode={field.mode}
					onChange={(value) => field.onChangeCheck && field.onChangeObject(value)}
					disabled={field.disabled}
					filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
					showSearch={field.showSearch}
					placeholder={field.placeholder}
					maxTagCount='responsive'
					tokenSeparators={field.tokenSeparators}
				>
					{field.options?.map((option, index) =>
						field.valueProperty && field.displayProperty ? (
							<Select.Option key={`select-${field.name}-${index}`} value={option[field.valueProperty]}>
								{option[field.displayProperty]}
							</Select.Option>
						) : (
							<Select.Option key={option} value={option}>
								{option}
							</Select.Option>
						)
					)}
				</Select>
			</Form.Item>
		)
	} else if (type === 'dependency') {
		return (
			<Form.Item
				key={field.name + 'dummy'}
				shouldUpdate={(prevValues, currentValues) => prevValues[field.independent] !== currentValues[field.independent]}
				noStyle
			>
				{({ getFieldValue }) =>
					field.condition.includes(getFieldValue(field.independent)) ? MultipleRenderSchema(field.successSchema) : MultipleRenderSchema(field.failureSchema)
				}
			</Form.Item>
		)
	} else if (type === 'recentSelect') {
		return (
			<Form.Item
				key={field.name}
				label={field.label}
				name={field.name}
				rules={[{ required: field.required, message: field.message }]}
				initialValue={field.initialValue}
				tooltip={field.tooltip}
			>
				<Select
					className='SelectField'
					suffixIcon={() => <CaretDownOutlined />}
					filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
					showSearch={field.showSearch}
					placeholder={field.placeholder}
					onChange={(event) => {
						const SelectedOption = JSON.parse(event)
						let NewObject = {}
						field.setFields?.forEach((setField) => {
							if (SelectedOption[setField]) {
								NewObject = { ...NewObject, [setField]: SelectedOption[setField] }
							}
						})
						if (field.selfValueProperty) {
							NewObject = { ...NewObject, [field.name]: SelectedOption[field.selfValueProperty] }
						}
						field.formReference.setFieldsValue(NewObject)
					}}
				>
					{field.options?.map((option, index) => (
						<Select.Option key={index} value={JSON.stringify(option)}>
							{option[field.displayProperty]}
						</Select.Option>
					))}
				</Select>
			</Form.Item>
		)
	} else if (type === 'radioGroup') {
		return (
			<Form.Item
				key={field.name}
				name={field.name}
				initialValue={field.initialValue}
				rules={[{ required: field.required, message: field.message }]}
				tooltip={field.tooltip}
			>
				{field.radioButtonView === true ? (
					<Radio.Group className='FormRadioGroup' disabled={field.disabled}>
						{Object.values(field.dataObject).map((type) => (
							<Radio.Button className='AutoHeight' value={type.key}>
								<div className='FlexColumnBox'>
									<p className='BoldFont HalfMarginTop'>{type.long}</p>
								</div>
							</Radio.Button>
						))}
					</Radio.Group>
				) : (
					<Radio.Group disabled={field.disabled}>
						{Object.values(field.dataObject).map((type, index) => (
							<Radio key={index} value={type.key}>
								{type.long}
							</Radio>
						))}
					</Radio.Group>
				)}
			</Form.Item>
		)
	} else if (type === 'vehicleRegistration') {
		return (
			<Form.Item
				key={field.name}
				label={field.label}
				name={field.name}
				validateTrigger={['onBlur']}
				rules={[
					{
						required: field.required,
						validator: (_, value = '') => {
							const specials = /^[a-zA-Z]+[0-9]+$/g
							if (value?.toString().length > 0) {
								if (specials.test(value.toString())) return Promise.resolve()
								else return Promise.reject(new Error(`Vehicle number must have alphabets followed by numbers.`))
							} else if (!field.required) return Promise.resolve(value)
							else return Promise.reject(new Error(field.message))
						},
					},
				]}
				tooltip={field.tooltip}
				initialValue={field.initialValue}
			>
				<Input
					className='InputField AddOn'
					placeholder='e.g abc123'
					onInput={(e) => {
						if (!'abcdefghijklmnopqrstuvwxyz0123456789'.includes(e.nativeEvent.data?.toLowerCase())) {
							e.target.value = e.target.value.replace(e.nativeEvent.data, '')
						}
					}}
					disabled={field.disabled}
				/>
			</Form.Item>
		)
	} else if (type === 'dateTime') {
		return (
			<Form.Item
				key={field.name}
				name={field.name}
				label={field.label}
				rules={[{ required: field.required, message: field.message }]}
				tooltip={field.tooltip}
				initialValue={field.initialValue}
			>
				<DatePicker
					className='InputField'
					defaultValue={field.defaultValue}
					disabled={field.disabled}
					disabledDate={field.disabledDate}
					disabledTime={field.disabledTime}
					format={field.format}
					placeholder={field.placeholder}
					showNow={field.showNow}
					showTime={
						field.showTime !== false && {
							defaultValue: field.defaultTimeValue,
							minuteStep: field.minuteStep,
						}
					}
					inputReadOnly={field.inputReadOnly}
				/>
			</Form.Item>
		)
	} else if (type === 'cascader') {
		return (
			<Form.Item
				key={field.name}
				label={field.label}
				name={field.name}
				rules={[{ required: field.required, message: field.message }]}
				tooltip={field.tooltip}
				initialValue={field.initialValue}
			>
				<Cascader className='CascaderField' options={field.options} expandTrigger='hover' />
			</Form.Item>
		)
	} else if (type === 'dynamicFields') {
		var number = 1
		return (
			<Form.List key={field.name} name={field.name} label={field.label} initialValue={field.initialValue}>
				{(fields, { add, remove }) => (
					<>
						{fields.map((singleEntryField, index) => (
							<div>
								<p className='BoldFont FontSize16 CenterAlign DarkGrayColor HalfMarginBottom'>{index + 1}.</p>
								<div key={singleEntryField.key} className='FlexRowSpace FullWidth' style={{ alignItems: 'flex-start' }}>
									{field.groups.map((group, index) => (
										<div className={`FullWidth ${index !== field.groups.length - 1 ? `MarginRight` : ``}`}>
											<p className='BoldFont FontSize16 PrimaryColor HalfMarginBottom'>{group.label}</p>
											{group.fields.map((item, index) => (
												<div className={`FullWidth`}>{RenderField({ ...item, name: [singleEntryField.name, item.name] })}</div>
											))}
										</div>
									))}
									{fields.length > field.minEntries && (
										<MinusCircleTwoTone
											twoToneColor='#FF0000'
											className='MarginLeft'
											onClick={() => {
												number = number - 1
												remove(singleEntryField.name)
											}}
										/>
									)}
								</div>
								<Divider />
							</div>
						))}
						{(field.maxEntries ? number < field.maxEntries : true) && (
							<Form.Item noStyle>
								<Button
									icon={<PlusOutlined />}
									className='Button DashedButton'
									onClick={() => {
										number = number + 1
										add()
									}}
									block
								>
									{fields.length === 0 ? 'Add ' : 'Add Another'} {field.label}
								</Button>
							</Form.Item>
						)}
					</>
				)}
			</Form.List>
		)
	} else if (type === 'checkbox') {
		return (
			<Form.Item
				key={field.name}
				name={field.name}
				rules={[{ required: field.required, message: field.message }]}
				tooltip={field.tooltip}
				className='HalfMarginBottom'
			>
				<Checkbox
					onChange={(e) => {
						if (Object.values(field.fillData).length > 0) {
							if (e.target.checked) {
								field.formReference.setFieldsValue(field.fillData)
							} else {
								field.formReference.setFieldsValue(field.clearData)
							}
						}
					}}
					defaultChecked={field.defaultChecked}
				>
					{field.label}
				</Checkbox>
			</Form.Item>
		)
	} else if (type === 'checkboxGroup') {
		return (
			<Form.Item
				key={field.name}
				name={field.name}
				rules={[{ required: field.required, message: field.message }]}
				tooltip={field.tooltip}
				className='HalfMarginBottom'
			>
				<CheckboxGroup
					options={field.options}
					className='FlexColumn HalfMargin'
					onChange={(event) => field.formReference.setFieldsValue({ [field.name]: event })}
					defaultValue={field.values}
				/>
				{console.log('')}
			</Form.Item>
		)
	} else if (type === 'statement') {
		return (
			<Form.Item key={field.name} name={field.name} className='HalfMarginBottom'>
				<p className='BoldFont FontSize16'>{field.sentence}</p>
			</Form.Item>
		)
	} else if (type === 'upload') {
		const UploadProps = {
			listType: 'picture',
			maxCount: field.maxCount,
			multiple: field.multiple,
			accept: UPLOAD_ACCEPT,
			beforeUpload: beforeUpload,
			customRequest: DocumentUpload,
			onChange: ChangeFileList,
		}
		return (
			<Form.Item
				key={field.name}
				label={field.label}
				name={field.name}
				rules={[{ required: field.required, message: field.message }]}
				tooltip={field.tooltip}
				valuePropName='fileList'
				getValueFromEvent={normFile}
			>
				{field.uploadType === 'button' ? (
					<Upload {...UploadProps}>
						<Button block>
							<div className='FlexRow'>
								<Icon className='Icon PrimaryColor HalfMarginRight' path={mdiUpload} />
								<p className='MarginLeft' style={{ marginLeft: 12 }}>
									{field.uploadMessage}
								</p>
							</div>
						</Button>
					</Upload>
				) : (
					<Upload.Dragger {...UploadProps}>
						<div className='flex items-center justify-center h-[20px]'>
							<Icon className='text-primary-800 mr-3' path={mdiUpload} size={0.9} />
							<p className='text-primary-800 font-bold text-sm'>{field.uploadMessage}</p>
						</div>
					</Upload.Dragger>
				)}
			</Form.Item>
		)
	}
}

export const RenderSchema = (Schema) =>
	Schema.map((SchemaObject) =>
		SchemaObject.fields ? (
			<>
				{/* <Divider className='SectionTitle' orientation='left' plain>{SchemaObject.title}</Divider>
			<p className='TitleDescription HalfMarginBottom'>{SchemaObject.description}</p> */}
				{SchemaObject.fields.map((Field, index) => (checkArray(Field) ? RowRenderer(Field) : RenderField(Field)))}
			</>
		) : (
			RenderField(SchemaObject)
		)
	)

export const RowRenderer = (Fields) => (
	<div className='FlexRow'>
		{Fields.map((Field, index) => (
			<div className={`FullWidth ${index !== 0 ? 'MarginLeft' : ''}`}>{RenderField(Field)}</div>
		))}
	</div>
)

export const MultipleRenderSchema = (Schema) => {
	return Schema.map((SchemaObject, index) => {
		return (
			<div key={index}>
				{!SchemaObject.hideLabel && <div className='text-base font-medium text-black-300 mb-3'>{SchemaObject.label}</div>}
				{!SchemaObject.fields ? (
					RenderField(SchemaObject)
				) : (
					<>
						{SchemaObject.fields.map((field, fieldIndex) => {
							return (
								<div className='w-full' key={fieldIndex}>
									{SchemaObject?.itemsPerRow !== SchemaObject?.fields?.length ? (
										(fieldIndex + 1) % 2 !== 0 && (
											<div className='flex items-start justify-between w-full'>
												{SchemaObject.fields[fieldIndex] && <div className='w-1/2 mr-3'>{RenderField(SchemaObject.fields[fieldIndex])}</div>}
												{SchemaObject.fields[fieldIndex + 1] && <div className='w-1/2 ml-1'>{RenderField(SchemaObject.fields[fieldIndex + 1])}</div>}
											</div>
										)
									) : (
										<div className='flex items-start justify-between w-full'>
											{(fieldIndex + 1) % SchemaObject?.itemsPerRow === 0 &&
												[...Array(SchemaObject?.itemsPerRow).keys()]?.reverse()?.map((item) => {
													return (
														SchemaObject.fields[fieldIndex - item] && (
															<div className={`w-1/${SchemaObject.itemsPerRow} mr-3`}>{RenderField(SchemaObject.fields[fieldIndex - item])}</div>
														)
													)
												})}
										</div>
									)}
								</div>
							)
						})}
					</>
				)}
				{!SchemaObject.hideDivider && <Divider />}
			</div>
		)
	})
}
